import { getResponsiveClasses } from '@athena-lib/responsive-utils';
import { gaps } from '@athena-lib/spacing';
import { objectEntries, objectFromEntries } from '@athena-lib/object-utils';
import type {
  GridColumnSpan,
  GridColumns,
  GridRowSpan,
  ResponseSpacing,
  ResponsiveGridColumnSpan,
  ResponsiveGridColumns,
  ResponsiveGridRowSpan,
  Spacing,
} from './types';
import { gridColumnSpansPerColumns, gridItemColumnSpans, gridRowSpans } from './constants';

export function getGapClassName(space: Spacing | ResponseSpacing | undefined) {
  return getResponsiveClasses(space, gaps);
}

export function getGridColumnSpanClasses(
  explicitGridItemColumnSpan: GridColumnSpan | ResponsiveGridColumnSpan | undefined,
  columns: GridColumns | ResponsiveGridColumns | undefined,
) {
  if (!columns) {
    return '';
  }

  if (explicitGridItemColumnSpan) {
    return getExplicitGridItemColumnSpanClasses(explicitGridItemColumnSpan, columns);
  }

  return getResponsiveClasses(
    typeof columns === 'number' ? columns : columns!,
    gridColumnSpansPerColumns,
  );
}

function getExplicitGridItemColumnSpanClasses(
  explicitGridItemColumnSpan: GridColumnSpan | ResponsiveGridColumnSpan,
  columns: GridColumns | ResponsiveGridColumns,
) {
  if (typeof columns === 'number') {
    return getExplicitGridItemColumnSpanClassesWithNumericColumns(
      explicitGridItemColumnSpan,
      columns,
    );
  } else {
    return getExplicitGridItemColumnSpanClassesWithResponsiveColumns(
      explicitGridItemColumnSpan,
      columns,
    );
  }
}

const getExplicitGridItemColumnSpanClassesWithNumericColumns = (
  explicitGridItemColumnSpan: GridColumnSpan | ResponsiveGridColumnSpan,
  columns: GridColumns,
) => {
  if (typeof explicitGridItemColumnSpan === 'number') {
    const columnFactor = 12 / columns;
    return getResponsiveClasses(
      (explicitGridItemColumnSpan * columnFactor).toString(),
      gridItemColumnSpans,
    );
  } else {
    const columnFactor = 12 / columns;
    return getResponsiveClasses(
      objectFromEntries(
        objectEntries(explicitGridItemColumnSpan).map(([deviceName, span]) => [
          deviceName,
          (span! * columnFactor).toString(),
        ]),
      ),
      gridItemColumnSpans,
    );
  }
};

const getExplicitGridItemColumnSpanClassesWithResponsiveColumns = (
  explicitGridItemColumnSpan: GridColumnSpan | ResponsiveGridColumnSpan,
  columns: ResponsiveGridColumns,
) => {
  if (typeof explicitGridItemColumnSpan === 'number') {
    return getResponsiveClasses(
      objectFromEntries(
        objectEntries(columns).map(([deviceName, columns]) => {
          const columnFactor = 12 / columns!;
          return [deviceName, (explicitGridItemColumnSpan! * columnFactor).toString()];
        }),
      ),
      gridItemColumnSpans,
    );
  } else {
    return getResponsiveClasses(
      objectFromEntries(
        objectEntries(columns).map(([deviceName, columns]) => {
          const columnFactor = 12 / columns!;
          const span = explicitGridItemColumnSpan[deviceName] ?? 1;
          return [deviceName, (span * columnFactor).toString()];
        }),
      ),
      gridItemColumnSpans,
    );
  }
};

export function getGridRowSpanClasses(rowSpan: GridRowSpan | ResponsiveGridRowSpan | undefined) {
  if (!rowSpan) {
    return '';
  }
  return getResponsiveClasses(rowSpan, gridRowSpans);
}
